import { runtimeEnv } from "@/lib/runtime-utils";
import { RestApiResponse } from "@/types/common.api";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { AutomationProps, Scene, ProjectType } from "@/types/type";
import {
  FetchFolderStatusDto,
  FolderStatusDto,
  GetProofreadJobDto,
  MutateSpeakerSceneDto,
  ProofreadJobDto,
  RequestProofreadingDto,
  RegenerateSpeakerSceneDto,
  AsyncJobDto, AsyncJobIdDto, ProofreadStepDto,
} from "@/types/translateVideo/dto";
import type {
  AutomationStreamingCaption,
  AutomationModel,
  AutomationStreamingCreateParams,
  AutomationStreamingDocument,
  AutomationStreamingMediaInfo,
  AutomationStreamingMediaInfoResponse,
} from "@/types/automation";

export const automationApi = createApi({
  reducerPath: "automationApi",
  refetchOnFocus: false,

  baseQuery: fetchBaseQuery({
    baseUrl: `${runtimeEnv.BK_STUDIO_URL}/api/automation`,
  }),
  tagTypes: ["automation"],

  endpoints: (builder) => ({
    createProject: builder.mutation<RestApiResponse<any>, AutomationProps>({
      query: (body) => ({
        url: "/create",
        method: "POST",
        body,
      }),
    }),
    getTemplatePost: builder.mutation<
      RestApiResponse<any>,
      {
        category: string;
        automation?: boolean;
        screenType?: string;
        orientation?: string;
        language?: string;
        useMyAvatar?: string;
      }
    >({
      query: ({
        category,
        automation,
        screenType,
        orientation,
        language,
        useMyAvatar,
      }) => ({
        url: `/templates`,
        method: "POST",
        body: {
          category,
          automation,
          screenType,
          orientation,
          language,
          useMyAvatar,
        },
      }),
      transformResponse: async (res: RestApiResponse<any>) => {
        if (!res.success) {
          return {
            success: false,
            error: res.error,
          };
        }
        return {
          success: true,
          data: res.data,
        };
      },
    }),
    createAutomationLog: builder.mutation<
      RestApiResponse<any>,
      {
        projectId: string;
        eventName: string;
        [key: string]: any;
      }
    >({
      query: (body) => ({
        url: "/automation-log",
        method: "POST",
        body,
      }),
      transformResponse: async (res: RestApiResponse<any>) => {
        if (!res.success || !res.data) {
          return [];
        }
        return res.data;
      },
    }),
    getCurrentlyActiveSeries: builder.query<RestApiResponse<any>, any>({
      query: (params) => ({
        url: "/videoSeries/count-active-series",
        method: "GET",
        params,
      }),
    }),
    tiktokAuthentication: builder.mutation({
      query: () => ({
        url: "/tiktok/authenticate",
        method: "GET",
      }),
    }),
    tiktokDisconnect: builder.mutation({
      query: () => ({
        url: "/tiktok/disconnect",
        method: "POST",
      }),
    }),
    tiktokGetToken: builder.mutation<RestApiResponse<any>, any>({
      query: (body) => ({
        url: "/tiktok/get-token",
        method: "POST",
        body,
      }),
    }),
    tiktokCreatorInfo: builder.mutation<RestApiResponse<any>, any>({
      query: () => ({
        url: "/tiktok/creator-info",
        method: "POST",
      }),
    }),
    tiktokPost: builder.mutation<RestApiResponse<any>, any>({
      query: (body) => ({
        url: "/tiktok/direct-post",
        method: "POST",
        body,
      }),
    }),
    tiktokGetPostStatus: builder.mutation<RestApiResponse<any>, any>({
      query: (body) => ({
        url: "/tiktok/get-post-status",
        method: "POST",
        body,
      }),
    }),
    extractFileImages: builder.mutation<
      RestApiResponse<any>,
      { azure: string; originalFileName: string }
    >({
      query: (body) => ({
        url: "/extract-file-images",
        method: "POST",
        body,
      }),
    }),
    googleAuthentication: builder.mutation({
      query: () => ({
        url: "/google/authenticate",
        method: "GET",
      }),
    }),
    googleDisconnect: builder.mutation({
      query: () => ({
        url: "/google/disconnect",
        method: "POST",
      }),
    }),
    googleGetToken: builder.mutation<RestApiResponse<any>, any>({
      query: (code) => ({
        url: `/google/get-token?code=${encodeURIComponent(code)}`,
        method: "GET",
      }),
    }),
    getOauth2LoginStatus: builder.query<RestApiResponse<any>, any>({
      query: () => ({
        url: "/get-oauth2-login-status",
        method: "GET",
      }),
    }),
    getMyGoogleChannelInfo: builder.query<RestApiResponse<any>, any>({
      query: () => ({
        url: "/google/get-my-channel",
        method: "GET",
      }),
    }),
    getGoogleCategoryList: builder.query<RestApiResponse<any>, any>({
      query: () => ({
        url: "/google/get-category-list",
        method: "GET",
      }),
    }),
    // 비디오 번역 관련
    /**
     * Srt file upload
     */
    uploadSrtFile: builder.mutation({
      query: (body) => ({
        url: "/translatevideo/upload-srt",
        method: "POST",
        body,
      }),
    }),
    /*
     * ProofreadingJob을 가져오는 GET API
     */
    getProofreadJob: builder.query<
      RestApiResponse<ProofreadJobDto | ProofreadStepDto>,
      GetProofreadJobDto
    >({
      query: (body) => ({
        url:
          "translatevideo/speaker-scenes?projectId=" +
          body.projectId +
          "&automationJobId=" +
          body.automationJobId,
        method: "GET",
      }),
    }),
    /*
     * ProofreadingJob을 업데이트하는 PUT API
     */
    updateSpeakerScenes: builder.mutation<
      RestApiResponse<any>,
      MutateSpeakerSceneDto
    >({
      query: (body) => ({
        url: "translatevideo/speaker-scenes",
        method: "PUT",
        body,
      }),
    }),
    /*
     * 선택된 SpeakerScene을 다시 발화시키는 비동기 POST API
     */
    regenerateSpeakerScenes: builder.mutation<
      RestApiResponse<AsyncJobIdDto>,
      RegenerateSpeakerSceneDto
    >({
      query: (body) => ({
        url: "translatevideo/speaker-scenes",
        method: "POST",
        body,
      }),
    }),
    /*
     * SpeakerScene 재발화 Job의 상태를 가져오는 GET API
     */
    fetchRegenerateJobStatus: builder.query<
      RestApiResponse<AsyncJobDto>,
      { jobId: string }
    >({
      query: (body) => ({
        url: "translatevideo/speaker-scenes?jobId=" + body.jobId,
        method: "GET",
      }),
    }),
    /*
     * 같은 folderId를 가진 프로젝트들의 완료 상태를 가져오는 GET API
     */
    fetchFolderStatus: builder.query<
      RestApiResponse<FolderStatusDto[]>,
      FetchFolderStatusDto
    >({
      query: (body) => ({
        url: "translatevideo/project?folderId=" + body.folderId,
        method: "GET",
      }),
    }),
    /*
     * Proofreading을 요청하고 export하는 POST API
     */
    requestProofreading: builder.mutation<
      RestApiResponse<any>,
      RequestProofreadingDto
    >({
      query: (body) => ({
        url: "/translatevideo/project",
        method: "POST",
        body,
      }),
    }),
    getAutomationStreamingTemplateList: builder.query<{ _id: string; thumbnailUrl: string }[], { category?: string; screenType?: string; language?: string; useBackground?: boolean }> ({
      query: (body) => {
        const searchParams = (() => {
          const params = Object.entries(body).reduce((acc, [key, value]) => {
            if (!value) {
              return acc;
            }
            return { ...acc, [key]: value };
          }, {});

          if (Object.keys(params).length === 0) {
            return "";
          }

          return `?${new URLSearchParams(params)}`;
        })();

        return {
          url: `/templates${searchParams}`,
          method: "GET",
        }
      },
      transformResponse: async (res: RestApiResponse<{ _id: string; thumbnailUrl: string }[]>) => {
        if (!res.success) {
          return [];
        }
        return res.data;
      },
    }),
    getAutomationStreamingModelList: builder.query<AutomationModel[], { locale?: string }> ({
      query: (body) => {
        const searchParams = (() => {
          const params = Object.entries(body).reduce((acc, [key, value]) => {
            if (!value) {
              return acc;
            }
            return { ...acc, [key]: value };
          }, {});

          if (Object.keys(params).length === 0) {
            return "";
          }

          return `?${new URLSearchParams(params)}`;
        })();

        return {
          url: `/models${searchParams}`,
          method: "GET",
        }
      },
      transformResponse: async (res: RestApiResponse<AutomationModel[]>) => {
        if (!res.success) {
          return [];
        }
        return res.data;
      },
    }),
    getAutomationStreamingProject: builder.query<RestApiResponse<{ project: ProjectType, job: AutomationStreamingDocument }>, string> ({
      query: (projectId) => ({
        url: `/streaming/${projectId}?request_type=project`,
        method: "GET",
      }),
    }),
    createAutomationStreamingProject: builder.mutation<RestApiResponse<{ projectId: string }>, AutomationStreamingCreateParams> ({
      query: (body) => ({
        url: "/streaming",
        method: "POST",
        body: { ...body, requestType: "create" },
      }),
    }),
    resetAutomationStreamingProject: builder.mutation<RestApiResponse<{ projectId: string }>, AutomationStreamingCreateParams & { projectId: string }> ({
      query: (body) => ({
        url: "/streaming",
        method: "POST",
        body: { ...body, requestType: "reset" },
      }),
    }),
    exportEarlyAutomationStreamingProject: builder.mutation<RestApiResponse<void>, { projectId: string }> ({
      query: (body) => ({
        url: "/streaming",
        method: "POST",
        body: { ...body, requestType: "exportEarly" },
      }),
    }),
    completeAutomationStreamingProject: builder.mutation<RestApiResponse<void>, { projectId: string; scenes: Scene[]; mediaInfo: AutomationStreamingMediaInfo[] }> ({
      query: (body) => {
        const { projectId, ...rest } = body;
        return {
          url: `/streaming/${projectId}`,
          method: "PUT",
          body: { ...rest, requestType: "complete" },
        }
      },
    }),
    updateAutomationStreamingProject: builder.mutation<RestApiResponse<void>, { projectId: string; scene: Scene }> ({
      query: (body) => {
        const { projectId, ...rest } = body;
        return {
          url: `/streaming/${projectId}`,
          method: "PUT",
          body: { ...rest, requestType: "update" },
        }
      },
    }),
    sealAutomationStreamingProject: builder.mutation<RestApiResponse<void>, { projectId: string; }> ({
      query: (body) => {
        const { projectId, ...rest } = body;
        return {
          url: `/streaming/${projectId}`,
          method: "PUT",
          body: { ...rest, requestType: "seal" },
        }
      },
    }),
    updateAutomationStreamingThumbnail: builder.mutation<RestApiResponse<void>, { projectId: string; thumbnail: string }> ({
      query: (body) => {
        const { projectId, ...rest } = body;
        return {
          url: `/streaming/${projectId}`,
          method: "PUT",
          body: { ...rest, requestType: "thumbnail" },
        }
      },
    }),
    requestAutomationStreamingCaption: builder.mutation<RestApiResponse<{ taskId: string }>, { projectId: string; scene: Scene; }> ({
      query: (body) => {
        const { projectId, ...rest } = body;
        return {
          url: `/streaming/caption/${projectId}`,
          method: "POST",
          body: rest,
        }
      },
    }),
    getAutomationStreamingCaption: builder.query<RestApiResponse<AutomationStreamingCaption>, { projectId: string; taskId: string }> ({
      query: (body) => {
        const { projectId, taskId } = body;
        return {
          url: `/streaming/caption/${projectId}/${taskId}`,
          method: "GET",
        }
      },
    }),
    // requestAutomationStreamingAudios: builder.mutation<RestApiResponse<{ taskId: string }>, { projectId: string; scene: Scene; }> ({
    //   query: (body) => {
    //     const { projectId, ...rest } = body;
    //     return {
    //       url: `/streaming/audios/${projectId}`,
    //       method: "POST",
    //       body: rest,
    //     }
    //   },
    // }),
    // getAutomationStreamingAudios: builder.query<RestApiResponse<Audios>, { projectId: string; taskId: string }> ({
    //   query: (body) => {
    //     const { projectId, taskId } = body;
    //     return {
    //       url: `/streaming/audios/${projectId}/${taskId}`,
    //       method: "GET",
    //     }
    //   },
    // }),
    getNewsList: builder.query<RestApiResponse<{ urlList: string[] }>, { locale: string; keyword: string; timeRange: number; count: number }> ({
      query: (body) => {
        const { locale, keyword, timeRange, count } = body;
        return {
          url: `/get-news-list?locale=${locale}&keyword=${keyword}&timeRange=${timeRange}&count=${count}`,
          method: "GET",
        }
      },
    }),
    getTrend: builder.query<RestApiResponse<{ trend: string }>, { locale: string; category: string; timeRange: number; }> ({
      query: (body) => {
        const { locale, category, timeRange } = body;
        return {
          url: `/get-category?locale=${locale}&category=${category}&timeRange=${timeRange}`,
          method: "GET",
        }
      },
    }),
    getMediaInfo: builder.query<RestApiResponse<AutomationStreamingMediaInfoResponse>, { projectId: string }> ({
      query: (body) => {
        const { projectId } = body;
        return {
          url: `/streaming/media-info/${projectId}`,
          method: "GET",
        }
      },
    }),
    updateMediaInfo: builder.mutation<RestApiResponse<void>, { projectId: string; mediaInfo: AutomationStreamingMediaInfo | null; sceneId: string; clipId: string; replacedId?: string }> ({
      query: (body) => {
        const { projectId, ...rest } = body;
        return {
          url: `/streaming/media-info/${projectId}`,
          method: "POST",
          body: rest,
        }
      },
    }),
  }),
});

export const {
  useCreateProjectMutation,
  useGetTemplatePostMutation,
  useCreateAutomationLogMutation,
  useGetCurrentlyActiveSeriesQuery,
  useTiktokAuthenticationMutation,
  useTiktokDisconnectMutation,
  useTiktokGetTokenMutation,
  useTiktokCreatorInfoMutation,
  useTiktokPostMutation,
  useTiktokGetPostStatusMutation,
  useExtractFileImagesMutation,
  useGoogleAuthenticationMutation,
  useGoogleDisconnectMutation,
  useGoogleGetTokenMutation,
  useGetOauth2LoginStatusQuery,
  useLazyGetOauth2LoginStatusQuery,
  useGetMyGoogleChannelInfoQuery,
  useLazyGetMyGoogleChannelInfoQuery,
  useGetGoogleCategoryListQuery,
  useLazyGetGoogleCategoryListQuery,
  useUploadSrtFileMutation,
  useGetProofreadJobQuery,
  useLazyGetProofreadJobQuery,
  useUpdateSpeakerScenesMutation,
  useLazyFetchRegenerateJobStatusQuery,
  useRegenerateSpeakerScenesMutation,
  useFetchFolderStatusQuery,
  useRequestProofreadingMutation,
  useGetAutomationStreamingTemplateListQuery,
  useLazyGetAutomationStreamingTemplateListQuery,
  useGetAutomationStreamingModelListQuery,
  useLazyGetAutomationStreamingProjectQuery,
  useCreateAutomationStreamingProjectMutation,
  useResetAutomationStreamingProjectMutation,
  useExportEarlyAutomationStreamingProjectMutation,
  useCompleteAutomationStreamingProjectMutation,
  useUpdateAutomationStreamingProjectMutation,
  useSealAutomationStreamingProjectMutation,
  useUpdateAutomationStreamingThumbnailMutation,
  useRequestAutomationStreamingCaptionMutation,
  useLazyGetAutomationStreamingCaptionQuery,
  // useRequestAutomationStreamingAudiosMutation,
  // useLazyGetAutomationStreamingAudiosQuery,
  useLazyGetNewsListQuery,
  useLazyGetTrendQuery,
  useLazyGetMediaInfoQuery,
  useUpdateMediaInfoMutation,
} = automationApi;

export default automationApi;
